import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Token storage
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('auth_token');
  }
}

export function getAuthToken(): string | null {
  if (!authToken) {
    authToken = localStorage.getItem('auth_token');
  }
  return authToken;
}

export function clearAuthToken() {
  authToken = null;
  localStorage.removeItem('auth_token');
}

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================
// Types
// ==================

export interface User {
  id: string;
  username: string;
  name: string;
  role: string;
}

export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface SensorCondition {
  sensor_device_id: string;
  sensor_register: string;
  operator: string;
  threshold: number;
}

export interface Blend {
  id: string;
  name: string;
  required_roast_time_seconds: number;
  target_color_rgb?: [number, number, number];
  target_roast_score?: number;
  start_condition?: SensorCondition | null;
  end_condition?: SensorCondition | null;
  created_at: string;
  updated_at: string;
}

export interface RoastBatch {
  id: string;
  blend_id: string;
  blend_name: string;
  blend_target_color_rgb?: [number, number, number];
  blend_target_roast_score?: number;
  quantity: number;
  unit: string;
  status: 'planned' | 'in-progress' | 'done';
  plan_date: string;
  sort_order: number;
  start_time?: string;
  end_time?: string;
  operator_id?: string;
  machine_id?: string;
  timer_started_at?: string;
  timer_duration_seconds?: number;
  remaining_seconds?: number;
  timer_running?: boolean;
  actual_roast_label?: string;
  actual_color_rgb?: [number, number, number];
  color_labeled_at?: string;
  color_label_deadline?: string;
  created_at: string;
  updated_at: string;
}

export interface SensorReading {
  device_id: string;
  register_address: string;
  register_name: string;
  register_type: string;
  value: string;
  time: string;
}

export interface DailyPlan {
  plan_date: string;
  batches: RoastBatch[];
  summary: {
    total: number;
    planned: number;
    in_progress: number;
    done: number;
    total_quantity: number;
  };
}

export interface BlendStats {
  blend_id: string;
  blend_name: string;
  total_batches: number;
  done_batches: number;
  planned_batches: number;
  in_progress_batches: number;
  total_quantity: number;
  done_quantity: number;
  avg_roast_duration_seconds: number | null;
  required_roast_time_seconds: number | null;
}

export interface OvenBlendBreakdown {
  blend_name: string;
  count: number;
  quantity: number;
}

export interface OvenStats {
  machine_id: string;
  total_batches: number;
  done_batches: number;
  in_progress_batches: number;
  total_quantity: number;
  done_quantity: number;
  avg_roast_duration_seconds: number | null;
  total_roast_time_seconds: number;
  blend_breakdown: OvenBlendBreakdown[];
}

export interface AnalyticsData {
  start_date: string;
  end_date: string;
  total_batches: number;
  blend_stats: BlendStats[];
  oven_stats: OvenStats[];
}

export interface VisionEvent {
  id: string;
  session_id?: string;
  device_id: string;
  captured_at: string;
  roast_time_seconds?: number;
  calibrated: boolean;
  average_rgb?: number[];
  average_hsv?: number[];
  average_lab?: number[];
  brownness_score?: number;
  brownness_rate?: number;
  roast_level_score?: number;
  roast_level_label?: string;
  roast_level_histogram?: number[];
  predicted_cooled_rgb?: number[];
  target_cooled_rgb?: number[];
  target_distance?: number;
  stop_recommended: boolean;
  features: Record<string, unknown>;
  full_image_key?: string;
  roi_image_key?: string;
  full_image_url?: string;
  roi_image_url?: string;
}

export type VisionCommandType =
  | 'camera_settings.update'
  | 'calibration.capture'
  | 'calibration.apply'
  | 'calibration.auto'
  | 'calibration.levels'
  | 'calibration.set_labels'
  | 'levels.get'
  | 'image.capture'
  | 'roi.update'
  | 'roi.reset';

export interface VisionCommand {
  id: string;
  device_id: string;
  type: VisionCommandType;
  payload: Record<string, unknown>;
  status: 'queued' | 'dispatched' | 'succeeded' | 'failed';
  created_at: string;
  updated_at: string;
  dispatched_at?: string;
  completed_at?: string;
  created_by?: string;
  result?: Record<string, unknown>;
  error?: string;
}

export interface OvenConfig {
  machine_id: string;
  vision_device_id?: string;
  modbus_device_id?: string;
  automation_rules?: OvenTriggerRule[] | null;
  updated_at?: string;
}

export interface OvenTriggerRule {
  event_type: 'roast_start' | 'roast_end';
  register_name: string;
  operator: string;
  threshold: number;
  consecutive_samples_required: number;
  enabled: boolean;
}

export interface OvenAutomationRulesConfig {
  machine_id: string;
  rules: OvenTriggerRule[];
}

export interface ServiceLogs {
  services: string[];
  lines: number;
  log: string;
}

// ==================
// Auth API
// ==================

export async function login(username: string, password: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/login', { username, password });
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<User>('/auth/me');
  return response.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
  clearAuthToken();
}

// ==================
// Blends API
// ==================

export async function listBlends(): Promise<Blend[]> {
  const response = await apiClient.get<Blend[]>('/blends');
  return response.data;
}

export async function createBlend(
  name: string,
  requiredRoastTimeSeconds: number,
  targetColorRgb?: [number, number, number] | null,
  targetRoastScore?: number | null,
  startCondition?: SensorCondition | null,
  endCondition?: SensorCondition | null,
): Promise<Blend> {
  const response = await apiClient.post<Blend>('/blends', {
    name,
    required_roast_time_seconds: requiredRoastTimeSeconds,
    target_color_rgb: targetColorRgb ?? null,
    target_roast_score: targetRoastScore ?? null,
    start_condition: startCondition ?? null,
    end_condition: endCondition ?? null,
  });
  return response.data;
}

export async function updateBlend(
  blendId: string,
  updates: {
    name?: string;
    required_roast_time_seconds?: number;
    target_color_rgb?: [number, number, number] | null;
    target_roast_score?: number | null;
    start_condition?: SensorCondition | null;
    end_condition?: SensorCondition | null;
  },
): Promise<Blend> {
  const response = await apiClient.patch<Blend>(`/blends/${blendId}`, updates);
  return response.data;
}

export async function deleteBlend(blendId: string): Promise<void> {
  await apiClient.delete(`/blends/${blendId}`);
}

// ==================
// Batches API
// ==================

export async function getDailyPlan(planDate: string): Promise<DailyPlan> {
  const response = await apiClient.get<DailyPlan>(`/batches/daily/${planDate}`);
  return response.data;
}

export async function createBatch(blendId: string, quantity: number, unit: string, planDate: string): Promise<RoastBatch[]> {
  const response = await apiClient.post<RoastBatch[]>('/batches', {
    blend_id: blendId,
    quantity,
    unit,
    plan_date: planDate,
  });
  return response.data;
}

export async function updateBatch(batchId: string, updates: { quantity?: number; unit?: string; status?: string; sort_order?: number; blend_id?: string; blend_name?: string }): Promise<RoastBatch> {
  const response = await apiClient.patch<RoastBatch>(`/batches/${batchId}`, updates);
  return response.data;
}

export async function startBatch(batchId: string, machineId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/start`, { machine_id: machineId });
  return response.data;
}

export async function startTimer(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/start-timer`);
  return response.data;
}

export async function finishBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/finish`);
  return response.data;
}

export async function cancelBatch(batchId: string): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/cancel`);
  return response.data;
}

export async function reorderBatches(batchIds: string[]): Promise<RoastBatch[]> {
  const response = await apiClient.post<RoastBatch[]>('/batches/reorder', { batch_ids: batchIds });
  return response.data;
}

export async function deleteBatch(batchId: string): Promise<void> {
  await apiClient.delete(`/batches/${batchId}`);
}

// ==================
// Analytics API
// ==================

export async function getAnalytics(startDate: string, endDate: string): Promise<AnalyticsData> {
  const response = await apiClient.get<AnalyticsData>('/analytics', {
    params: { start_date: startDate, end_date: endDate },
  });
  return response.data;
}

// ==================
// Vision API
// ==================

export async function listVisionEvents(limit = 25): Promise<VisionEvent[]> {
  const response = await apiClient.get<VisionEvent[]>('/vision/events', { params: { limit } });
  return response.data;
}

export async function getLatestVisionEvent(deviceId?: string): Promise<VisionEvent> {
  const response = await apiClient.get<VisionEvent>('/vision/events/latest', {
    params: deviceId ? { device_id: deviceId } : undefined,
  });
  return response.data;
}

export async function listVisionDevices(): Promise<string[]> {
  const response = await apiClient.get<string[]>('/vision/devices');
  return response.data;
}

export async function getVisionEvent(eventId: string): Promise<VisionEvent> {
  const response = await apiClient.get<VisionEvent>(`/vision/events/${eventId}`);
  return response.data;
}

export async function createVisionCommand(
  deviceId: string,
  type: VisionCommandType,
  payload: Record<string, unknown> = {}
): Promise<VisionCommand> {
  const response = await apiClient.post<VisionCommand>(`/vision/devices/${deviceId}/commands`, { type, payload });
  return response.data;
}

export async function listVisionCommands(deviceId: string, limit = 25): Promise<VisionCommand[]> {
  const response = await apiClient.get<VisionCommand[]>(`/vision/devices/${deviceId}/commands`, { params: { limit } });
  return response.data;
}

export interface VisionDeviceStatus {
  device_id: string;
  last_event_at: string | null;
  last_command_interaction_at: string | null;
  pending_commands: number;
  dispatched_commands: number;
  recent_commands: { id: string; type: string; status: string; updated_at: string }[];
}

export async function getVisionDeviceStatus(deviceId: string): Promise<VisionDeviceStatus> {
  const response = await apiClient.get<VisionDeviceStatus>(`/vision/devices/${deviceId}/status`);
  return response.data;
}

// ==================
// Oven Config API
// ==================

export async function getOvenConfig(machineId: string): Promise<OvenConfig> {
  const response = await apiClient.get<OvenConfig>(`/ovens/${machineId}/config`);
  return response.data;
}

export async function setOvenConfig(
  machineId: string,
  visionDeviceId: string | null | undefined,
  modbusDeviceId?: string | null,
): Promise<OvenConfig> {
  const body: Record<string, string | null> = {};
  if (visionDeviceId !== undefined) body.vision_device_id = visionDeviceId;
  if (modbusDeviceId !== undefined) body.modbus_device_id = modbusDeviceId;
  const response = await apiClient.put<OvenConfig>(`/ovens/${machineId}/config`, body);
  return response.data;
}

export async function getModbusDevices(): Promise<{ device_id: string; last_seen: string }[]> {
  const response = await apiClient.get<{ devices: { device_id: string; last_seen: string }[]; count: number }>(
    '/sensors/devices',
  );
  return response.data.devices;
}

export async function getOvenAutomationRules(machineId: string): Promise<OvenAutomationRulesConfig> {
  const response = await apiClient.get<OvenAutomationRulesConfig>(`/ovens/${machineId}/automation-rules`);
  return response.data;
}

export async function setOvenAutomationRules(
  machineId: string,
  rules: OvenTriggerRule[],
): Promise<OvenAutomationRulesConfig> {
  const response = await apiClient.put<OvenAutomationRulesConfig>(`/ovens/${machineId}/automation-rules`, {
    machine_id: machineId,
    rules,
  });
  return response.data;
}

// ==================
// Logs API
// ==================

export async function getServiceLogs(services: string[], lines = 200): Promise<ServiceLogs> {
  const response = await apiClient.get<ServiceLogs>('/logs', {
    params: { services, lines },
    paramsSerializer: { indexes: null },
  });
  return response.data;
}

// ==================
// Sensor API
// ==================

export async function getSensorReadings(hours = 1, device?: string, register?: string): Promise<SensorReading[]> {
  const response = await apiClient.get<{ readings: SensorReading[]; count: number }>('/sensors/readings', {
    params: { hours, device, register },
  });
  return response.data.readings;
}

export async function getLatestSensorReadings(device?: string): Promise<SensorReading[]> {
  const response = await apiClient.get<{ latest: SensorReading[]; count: number }>('/sensors/latest', {
    params: device ? { device } : undefined,
  });
  return response.data.latest;
}

export async function labelBatchColor(
  batchId: string,
  roastLabel: string,
  actualColorRgb: [number, number, number],
): Promise<RoastBatch> {
  const response = await apiClient.post<RoastBatch>(`/batches/${batchId}/color-label`, {
    roast_label: roastLabel,
    actual_color_rgb: actualColorRgb,
  });
  return response.data;
}

export default apiClient;
